/*
 * TSOGN ERA GRAMMAR SYSTEM
 * 10 era tiers + 1 Navigation State
 * Each era defines: colors, fonts, frame style
 * Applied via CSS custom properties on [data-era] attribute
 */

const ERA_CONFIG = {
  // Navigation State — the time machine frame itself, belongs to no era
  nav: {
    id: 'nav',
    label: 'Navigation State',
    range: null,
    primary: '#2F8A92',
    accent: '#D4A84A',
    text: '#E0E0E0',
    textSecondary: '#A1A1AA',
    background: '#0A0A0A',
    contentFont: "'Cormorant Garamond', serif",
    headingFont: "'Cormorant Garamond', serif",
    logoColor: '#D4A84A',
    logoFont: "'Cormorant Garamond', serif",
    frameStyle: 'glassmorphism',
    frameBorder: 'rgba(47,138,146, 0.15)',
  },

  // Era 0: Deep Prehistoric (200,000 BCE – 3000 BCE)
  era0: {
    id: 'era0',
    label: 'Deep Prehistoric',
    range: [-200000, -3000],
    primary: '#8B6E4E',
    accent: '#6B4226',
    text: '#BCA88A',
    textSecondary: '#7A6B55',
    background: '#110E08',
    contentFont: "'IBM Plex Mono', monospace",
    headingFont: "'IBM Plex Mono', monospace",
    logoColor: '#8B6E4E',
    logoFont: "'IBM Plex Mono', monospace",
    frameStyle: 'raw-earth',
    frameBorder: 'rgba(139, 110, 78, 0.25)',
    note: 'Pre-literate era. Imagery carries weight. Text rendered as reconstruction, not period-native.',
  },

  // Era 1: Ancient (3000 BCE – 500 BCE)
  era1: {
    id: 'era1',
    label: 'Ancient',
    range: [-3000, -500],
    primary: '#C4956A',
    accent: '#8B6914',
    text: '#D4C4A8',
    textSecondary: '#9A8B70',
    background: '#1A1409',
    contentFont: "'Noto Serif', serif",
    headingFont: "'Cinzel Decorative', serif",
    logoColor: '#C4956A',
    logoFont: "'Cinzel Decorative', serif",
    frameStyle: 'stone',
    frameBorder: 'rgba(196, 149, 106, 0.2)',
  },

  // Era 2: Classical (500 BCE – 500 CE)
  era2: {
    id: 'era2',
    label: 'Classical',
    range: [-500, 500],
    primary: '#C75B3F',
    accent: '#B8860B',
    text: '#E8DDD0',
    textSecondary: '#A89A88',
    background: '#12100E',
    contentFont: "'Playfair Display', serif",
    headingFont: "'Cinzel', serif",
    logoColor: '#B8860B',
    logoFont: "'Cinzel', serif",
    frameStyle: 'marble',
    frameBorder: 'rgba(184, 134, 11, 0.2)',
  },

  // Era 3: Medieval (500 – 1500)
  era3: {
    id: 'era3',
    label: 'Medieval',
    range: [500, 1500],
    primary: '#1E3A5F',
    accent: '#C8A951',
    text: '#D6CCB0',
    textSecondary: '#968E78',
    background: '#0F0D08',
    contentFont: "'EB Garamond', serif",
    headingFont: "'Uncial Antiqua', serif",
    logoColor: '#C8A951',
    logoFont: "'Uncial Antiqua', serif",
    frameStyle: 'parchment',
    frameBorder: 'rgba(200, 169, 81, 0.2)',
  },

  // Era 4: Early Modern (1500 – 1800)
  era4: {
    id: 'era4',
    label: 'Early Modern',
    range: [1500, 1800],
    primary: '#6B5B3E',
    accent: '#8B7D3C',
    text: '#D4C9B0',
    textSecondary: '#948B70',
    background: '#100E09',
    contentFont: "'EB Garamond', serif",
    headingFont: "'EB Garamond', serif",
    logoColor: '#8B7D3C',
    logoFont: "'EB Garamond', serif",
    frameStyle: 'letterpress',
    frameBorder: 'rgba(107, 91, 62, 0.25)',
  },

  // Era 5: Industrial / Victorian (1800 – 1900)
  era5: {
    id: 'era5',
    label: 'Industrial',
    range: [1800, 1900],
    primary: '#6B6B5E',
    accent: '#B8963E',
    text: '#D8D2C4',
    textSecondary: '#8A8578',
    background: '#0E0D0B',
    contentFont: "'Old Standard TT', serif",
    headingFont: "'Old Standard TT', serif",
    logoColor: '#B8963E',
    logoFont: "'Old Standard TT', serif",
    frameStyle: 'broadsheet',
    frameBorder: 'rgba(107, 107, 94, 0.25)',
  },

  // Era 6: Early 20th Century (1900 – 1945)
  era6: {
    id: 'era6',
    label: 'Early 20th Century',
    range: [1900, 1945],
    primary: '#7A7560',
    accent: '#BFA84F',
    text: '#CEC8B8',
    textSecondary: '#8A8575',
    background: '#0D0C09',
    contentFont: "'Special Elite', monospace",
    headingFont: "'Special Elite', monospace",
    logoColor: '#BFA84F',
    logoFont: "'Special Elite', monospace",
    frameStyle: 'newsprint',
    frameBorder: 'rgba(122, 117, 96, 0.25)',
  },

  // Era 7: Broadcast Era (1945 – 1980) — renamed from "Cold War"
  era7: {
    id: 'era7',
    label: 'Broadcast Era',
    range: [1945, 1980],
    primary: '#4A6B8A',
    accent: '#CC4444',
    text: '#C8C8C8',
    textSecondary: '#808080',
    background: '#0A0B0F',
    contentFont: "'DM Sans', sans-serif",
    headingFont: "'DM Sans', sans-serif",
    logoColor: '#4A6B8A',
    logoFont: "'DM Sans', sans-serif",
    frameStyle: 'broadcast',
    frameBorder: 'rgba(74, 107, 138, 0.25)',
  },

  // Era 8: Late 20th / Modern (1980 – 2000)
  era8: {
    id: 'era8',
    label: 'Late Modern',
    range: [1980, 2000],
    primary: '#2D8B6D',
    accent: '#E8A825',
    text: '#D0D0D0',
    textSecondary: '#808080',
    background: '#0A0A0E',
    contentFont: "'Space Grotesk', sans-serif",
    headingFont: "'Space Grotesk', sans-serif",
    logoColor: '#2D8B6D',
    logoFont: "'Space Grotesk', sans-serif",
    frameStyle: 'digital',
    frameBorder: 'rgba(45, 139, 109, 0.25)',
  },

  // Era 9: Contemporary (2000 – present) — lesson styling
  era9: {
    id: 'era9',
    label: 'Contemporary',
    range: [2000, 2100],
    primary: '#2F8A92',
    accent: '#D4A84A',
    text: '#E0E0E0',
    textSecondary: '#A1A1AA',
    background: '#0A0A0A',
    contentFont: "'Cormorant Garamond', serif",
    headingFont: "'Cormorant Garamond', serif",
    logoColor: '#e8d5a3',
    logoFont: "'Cormorant Garamond', serif",
    frameStyle: 'glassmorphism',
    frameBorder: 'rgba(47,138,146, 0.15)',
  },
};

/**
 * Get era config for a given year.
 * Returns era0-era9 based on year range.
 */
export function getEraForYear(year) {
  if (year == null) return ERA_CONFIG.era9;
  const eras = [
    ERA_CONFIG.era0, ERA_CONFIG.era1, ERA_CONFIG.era2, ERA_CONFIG.era3,
    ERA_CONFIG.era4, ERA_CONFIG.era5, ERA_CONFIG.era6, ERA_CONFIG.era7,
    ERA_CONFIG.era8, ERA_CONFIG.era9,
  ];
  for (const era of eras) {
    if (year >= era.range[0] && year < era.range[1]) return era;
  }
  if (year >= 2000) return ERA_CONFIG.era9;
  if (year < -200000) return ERA_CONFIG.era0;
  return ERA_CONFIG.era9;
}

/**
 * Get the Navigation State config (time machine frame).
 */
export function getNavState() {
  return ERA_CONFIG.nav;
}

/**
 * Generate CSS custom properties for an era.
 */
export function eraToCSS(era) {
  return {
    '--era-primary': era.primary,
    '--era-accent': era.accent,
    '--era-text': era.text,
    '--era-text-secondary': era.textSecondary,
    '--era-background': era.background,
    '--era-content-font': era.contentFont,
    '--era-heading-font': era.headingFont,
    '--era-logo-color': era.logoColor,
    '--era-logo-font': era.logoFont,
    '--era-frame-border': era.frameBorder,
  };
}

export default ERA_CONFIG;
